import { useCallback, useEffect, useState } from 'react'

const STORAGE_PREFIX = 'logargo.tableState.v1.'

interface PersistedState {
  hiddenColumns: string[]
  sort?: { field: string; dir: 'asc' | 'desc' }
  filters?: Record<string, unknown>
}

const empty: PersistedState = { hiddenColumns: [] }

function read(key: string): PersistedState {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + key)
    if (!raw) return empty
    const parsed = JSON.parse(raw)
    return {
      hiddenColumns: Array.isArray(parsed.hiddenColumns) ? parsed.hiddenColumns : [],
      sort: parsed.sort,
      filters: parsed.filters,
    }
  } catch {
    return empty
  }
}

function write(key: string, value: PersistedState) {
  try {
    localStorage.setItem(STORAGE_PREFIX + key, JSON.stringify(value))
  } catch {
    /* quota / private mode — silently swallow */
  }
}

/**
 * Persist a table's column visibility, sort, and filter state in localStorage
 * keyed by `tableId`. Use the same id for the same logical table across
 * sessions so users come back to the same layout.
 *
 * Returns helpers small enough to drop straight into JSX.
 */
export function usePersistedTableState(tableId: string) {
  const [state, setState] = useState<PersistedState>(() => read(tableId))

  useEffect(() => {
    write(tableId, state)
  }, [tableId, state])

  const isColumnHidden = useCallback(
    (column: string) => state.hiddenColumns.includes(column),
    [state.hiddenColumns]
  )

  const toggleColumn = useCallback((column: string) => {
    setState((prev) => {
      const has = prev.hiddenColumns.includes(column)
      return {
        ...prev,
        hiddenColumns: has
          ? prev.hiddenColumns.filter((c) => c !== column)
          : [...prev.hiddenColumns, column],
      }
    })
  }, [])

  const resetColumns = useCallback(() => {
    setState((prev) => ({ ...prev, hiddenColumns: [] }))
  }, [])

  const setSort = useCallback((field: string, dir: 'asc' | 'desc' | null) => {
    setState((prev) => ({
      ...prev,
      sort: dir == null ? undefined : { field, dir },
    }))
  }, [])

  const setFilters = useCallback((filters: Record<string, unknown>) => {
    setState((prev) => ({ ...prev, filters }))
  }, [])

  return {
    hiddenColumns: state.hiddenColumns,
    sort: state.sort,
    filters: state.filters,
    isColumnHidden,
    toggleColumn,
    resetColumns,
    setSort,
    setFilters,
  }
}
